// ============================================================
// Regroupement — les feuilles d'une pièce, dans les deux sens (4/11/2026).
// ------------------------------------------------------------
// Philippe garde la main : le programme ne regroupe tout seul que le cas
// certain, et PROPOSE le reste. Ici :
//  - la pièce courante dit ses feuilles et pourquoi elles sont ensemble,
//    avec « Séparer » ;
//  - les propositions disent ce qui diffère, avec « Regrouper » et
//    « Ignorer » ;
//  - « Regrouper à la main » : cocher des pièces, puis regrouper.
// Regrouper relit les feuilles ensemble (un appel) ; séparer rend à chaque
// feuille sa propre lecture, déjà faite.
// ============================================================

import { useState } from 'react';
import Button from '../../ui/Button.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { api, READ_TIMEOUT_MS } from '../../lib/api.js';
import { libelleFeuilles, libelleMotif, libelleEcarts } from './feuillesLibelles.js';

/** Un geste qui peut demander de confirmer une perte (409), comme la suppression d'un plan. */
async function avecConfirmation(envoyer) {
  try {
    return await envoyer(false);
  } catch (err) {
    if (err.status !== 409 || !err.details?.confirmation_requise || !window.confirm(err.message)) throw err;
    return envoyer(true);
  }
}

export default function Regroupement({ devisId, etat, pieces = [], courante = null, locked, avant, apres }) {
  const toast = useToast();
  const [occupe, setOccupe] = useState(false);
  const [coches, setCoches] = useState([]);
  if (!etat) return null;

  const nom = (id) => {
    const p = pieces.find((x) => x.piece?.id === id)?.piece;
    return libelleFeuilles(etat.pieces?.[id], p?.ref) ?? (p ? `${p.ref ?? ''} ${p.name ?? ''}`.trim() : `pièce ${id}`);
  };

  async function geste(fn, succes) {
    setOccupe(true);
    try {
      await avant?.();
      await fn();
      toast.success(succes);
      setCoches([]);
      apres?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setOccupe(false);
    }
  }

  const regrouper = (ids) => geste(
    () => avecConfirmation((confirmer) => api.post(`/devis/${devisId}/regroupements`,
      { pieces: ids, confirmer_perte: confirmer }, { timeout: READ_TIMEOUT_MS })),
    'Feuilles regroupées et relues ensemble.',
  );
  const separer = (id) => geste(
    () => avecConfirmation((confirmer) => api.post(`/devis/${devisId}/pieces/${id}/separation`, { confirmer_perte: confirmer })),
    'Pièce séparée : une pièce par feuille.',
  );
  const ignorer = (ids) => geste(() => api.post(`/devis/${devisId}/regroupements/ignorer`, { pieces: ids }), 'Proposition ignorée.');

  const id = courante?.piece?.id;
  const info = id ? etat.pieces?.[id] : null;
  const avecPlan = pieces.filter((p) => p.piece?.id && p.piece?.plan_file_id);

  return (
    <div className="regroupement stack">
      {info?.feuilles?.length > 1 ? (
        <div className="regroupement__courante row row--between">
          <p>
            <strong>{libelleFeuilles(info, courante.piece.ref)}</strong>
            {info.regroupement ? <span className="field__hint">{` ${libelleMotif(info.regroupement)}`}</span> : null}
          </p>
          {locked ? null : (
            <Button size="sm" variant="secondary" disabled={occupe} onClick={() => separer(id)}>Séparer</Button>
          )}
        </div>
      ) : null}

      {locked ? null : (etat.propositions ?? []).map((pr) => (
        <div key={pr.pieces.join('-')} className="regroupement__proposition row row--between" role="status">
          <p>
            Ces feuilles semblent décrire la même pièce : {pr.pieces.map(nom).join(' et ')} — Regrouper ?
            {pr.ecarts?.length ? <span className="field__hint">{` Ce qui diffère : ${libelleEcarts(pr.ecarts)}.`}</span> : null}
          </p>
          <div className="row">
            <Button size="sm" variant="secondary" disabled={occupe} onClick={() => regrouper(pr.pieces)}>Regrouper</Button>
            <Button size="sm" variant="ghost" disabled={occupe} onClick={() => ignorer(pr.pieces)}>Ignorer</Button>
          </div>
        </div>
      ))}

      {locked || avecPlan.length < 2 ? null : (
        <details className="repli repli--champs">
          <summary>Regrouper des pièces à la main</summary>
          <ul className="regroupement__liste">
            {avecPlan.map(({ piece }) => (
              <li key={piece.id}>
                <input type="checkbox" id={`rg-${piece.id}`} aria-label={`Regrouper ${nom(piece.id)}`} checked={coches.includes(piece.id)}
                  onChange={(e) => setCoches((c) => (e.target.checked ? [...c, piece.id] : c.filter((x) => x !== piece.id)))} />
                <label htmlFor={`rg-${piece.id}`}>{nom(piece.id)}{piece.name ? ` — ${piece.name}` : ''}</label>
              </li>
            ))}
          </ul>
          <Button size="sm" variant="secondary" disabled={occupe || coches.length < 2} onClick={() => regrouper(coches)}>
            {occupe ? 'Lecture des feuilles…' : `Regrouper la sélection (${coches.length})`}
          </Button>
        </details>
      )}
    </div>
  );
}
